import Advertising from '@components/advertising/advertising.component'
import ModalNotification from '@components/modal-notification/modal-notification.component'
import {
  ClarityService,
  DakenService,
  Environment,
  ErrorBoundary,
  HubConfig,
  LogService,
  MySquid,
  onLangChange,
  onUserChange,
  User,
  UserHelper,
} from '@hub/shared'
import DefaultLayout from '@layouts/default/default.layout'
import HelpPage from '@pages/help/help.page'
import NotificationsPage from '@pages/notifications/notifications.page'
import { onLangChange as onLangReactCss, SqDataLayerHelper } from '@squidit/react-css'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'
import i18n from './i18n.main'
import HomePage from './pages/home/home.page'
import WakeProductsPage from './pages/wake-products/wake-products.page'

const EXPIRATION_TIME = 1000 * 60 * 60 * 12

interface UserObject {
  id?: string | number
  username?: string
  email?: string
}

export default function Root(props: {
  configs: MySquid
  environments: Environment
  general: HubConfig
  mountParcel: any
  name: string
  singleSpa: any
}) {
  const [user, setUser] = useState<User | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const [activeParcel, setActiveParcel] = useState<string | null>(null)
  const mainRoutes = useMemo(
    () => ['login', 'home', 'help', 'notifications', 'profile', 'campaigns', 'dashboard', 'payments', 'payment', 'mediakit', 'legacy'],
    []
  )
  const userHelper = useMemo(() => new UserHelper(), [])
  const { t } = useTranslation()
  const dakenService = useMemo(() => new DakenService(), [])
  const clarityService = useMemo(() => new ClarityService(), [])
  const logService = useMemo(() => new LogService(), [])
  const dataLayerHelper = useMemo(() => new SqDataLayerHelper(), [])

  useEffect(() => {
    const onLangChangeObservable: SubscriptionLike = onLangChange.subscribe((lang: 'pt' | 'en' | 'es') => {
      i18n.changeLanguage(lang)
      onLangReactCss.next(lang)
    })
    return () => {
      onLangChangeObservable?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    const onUserChangeObservable: SubscriptionLike = onUserChange.subscribe((user: User) => {
      setUser(user)
      if (user?.squidId) {
        dataLayerHelper.sendToDataLayer({
          event: 'hub_user_conversion',
          user: {
            squidId: user?.squidId,
            username: user?.profileadditionalinfo?.name,
            email: user?.profileadditionalinfo?.email,
          },
        })
        // TODO: Utilizando a maneira genérica ao lançar este evento específico pois já existem métricas baseadas nesses dados com essa estrutura.
        // @ts-ignore
        window?.dataLayer.push(eventPayload)
        clarityService?.initClarity(user?.squidId)
      }
    })
    return () => {
      onUserChangeObservable?.unsubscribe()
    }
  }, [props?.environments, clarityService, dataLayerHelper])

  useEffect(() => {
    setTimeout(() => {
      const items = document.querySelectorAll(`#single-spa-application\\:\\@hub\\/${activeParcel}`)
      if (items?.length > 1) {
        for (let i = 1; i < items?.length; i++) {
          const element = items[i]
          if (element) {
            element.remove()
          }
        }
      }
    }, 1000)
  }, [activeParcel])

  const mount = useCallback(() => {
    const domElement = document.getElementById(`single-spa-application:@hub/${activeParcel}`)
    const thisWindow: any = window
    if (domElement && thisWindow[`@hub/${activeParcel}`]) {
      props.mountParcel(thisWindow[`@hub/${activeParcel}`], { ...props, domElement, mainRoutes })
    }
  }, [activeParcel, props, mainRoutes])

  const NoMatch = () => {
    history.pushState({}, '', '/error/not-found')
    return <div />
  }

  const ElementToRender = ({ name = '' }) => {
    useEffect(() => {
      setActiveParcel(name)
      mount()
    }, [name])
    return <div id={`single-spa-application:@hub/${name}`} />
  }

  useEffect(() => {
    const handleRouteChange = async () => {
      const userObject = await userHelper?.getUserObject()
      if (
        !userObject?.updatedAt ||
        (userObject?.JWT && userObject?.updatedAt && new Date().getTime() > userObject?.updatedAt + EXPIRATION_TIME)
      ) {
        try {
          const user = await dakenService.getInfluencersInfo(userObject?.JWT)
          await userHelper?.setUserObject(user, userObject?.JWT, userObject?.expiresAt)
        } catch (error) {
          logService?.error({ message: 'Error getting user info', error })
        }
      }
    }

    window.addEventListener('popstate', handleRouteChange)
    return () => {
      window.removeEventListener('popstate', handleRouteChange)
    }
  }, [dakenService, logService, userHelper])

  if (props?.general?.maintenance) {
    history.pushState({}, '', '/error/maintenance')
    return <div />
  }

  return (
    <ErrorBoundary fallback={t('globalMain:errorText')}>
      <Router>
        <DefaultLayout loading={!user?.squidId} {...props}>
          <Routes>
            <Route path="/" element={<Navigate to="/home" />} />
            <Route path="home" element={<HomePage />} />
            <Route path="help" element={<HelpPage {...props} />} />
            <Route path="profile/help" element={<Navigate to={{ pathname: '/help' }} />} />
            <Route path="notifications" element={<NotificationsPage {...props} />} />
            <Route path="dashboard/wake-products" element={<WakeProductsPage {...props} />} />
            <Route path="profile/*" element={<ElementToRender name="profile" />} />
            {['campaigns', 'dashboard']?.map((r) => {
              return <Route key={r} path={`${r}/*`} element={<ElementToRender name="campaigns" />} />
            })}
            {['payments', 'payment']?.map((r) => {
              return <Route key={r} path={`${r}/*`} element={<ElementToRender name="payments" />} />
            })}
            <Route path="mediakit/*" element={<ElementToRender name="mediakit" />} />
            <Route path="legacy/*" element={<Navigate to={window.location.pathname?.replace('/legacy', '')} />} />
            <Route path="login" />
            <Route path="*" element={<NoMatch />} />
          </Routes>
          {!props?.configs?._id && <Advertising className="mt-5 mb-3" />}
          {user && <ModalNotification open={open} setOpen={setOpen} squidId={user.squidId} />}
        </DefaultLayout>
      </Router>
    </ErrorBoundary>
  )
}
